interface Project {
  id: string;
  name: string;
  displayName: string;
  repo: string;
  description: string;
  type: string;
  status: string;
  language: string;
  color: string;
  platforms: string[];
  tags: string[];
  stars: number;
  forks: number;
  demoUrl: string;
  docsUrl: string;
  installCmd: string;
  featured: boolean;
  topics: string[];
}

export function renderProjects(): string {
  return /* html */`
<div class="page-wrapper projects-page">
  <div class="page-hero">
    <span class="section-label">GitHub Projects</span>
    <h1 class="hero-title anim-fade-up">
      Open source,<br/><span class="gradient-text">open process.</span>
    </h1>
    <p class="hero-mission anim-fade-up" style="animation-delay:0.1s">
      Browse all Xaebia repositories. Each project is a real, usable piece
      of software — not a toy, not a demo, not abandoned.
    </p>
    <div class="anim-fade-up" style="animation-delay:0.2s">
      <a href="https://github.com/xirtus" class="btn btn-secondary" target="_blank" rel="noopener">
        github.com/xirtus ↗
      </a>
    </div>
  </div>

  <div class="page-section">
    <!-- Type filter -->
    <div class="filter-bar" role="group" aria-label="Filter by type">
      <button class="filter-btn active" data-filter="all">All</button>
      <button class="filter-btn" data-filter="AI App">AI</button>
      <button class="filter-btn" data-filter="CLI Tool">CLI</button>
      <button class="filter-btn" data-filter="Web App">Web</button>
      <button class="filter-btn" data-filter="Desktop App">Desktop</button>
      <button class="filter-btn" data-filter="Game">Games</button>
      <button class="filter-btn" data-filter="Library">Libraries</button>
    </div>

    <div id="projects-grid" class="projects-grid stagger" style="margin-top:var(--s8)">
      <!-- Populated by initProjects() -->
    </div>
  </div>
</div>
`;
}

export function initProjects(): void {
  import('../data/projects.json').then(({ default: raw }) => {
    const projects = raw as Project[];
    const grid = document.getElementById('projects-grid');
    if (!grid) return;

    function render(filter: string): void {
      const filtered = filter === 'all'
        ? projects
        : projects.filter(p => p.type === filter);
      grid!.innerHTML = filtered.map(projectCard).join('');

      grid!.querySelectorAll('.project-card').forEach(card => {
        card.addEventListener('click', () => {
          const id = (card as HTMLElement).dataset.projectId;
          openProjectDetail(projects.find(p => p.id === id)!);
        });
      });
    }

    document.querySelectorAll('.filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        render((btn as HTMLElement).dataset.filter ?? 'all');
      });
    });

    render('all');
  });
}

function projectCard(p: Project): string {
  const langColor: Record<string, string> = {
    Python: '#3776ab', TypeScript: '#3178c6', Rust: '#ce422b', JavaScript: '#f7df1e',
  };
  const lc = langColor[p.language] ?? '#8b5cf6';

  return /* html */`
  <article class="project-card card" data-project-id="${p.id}" tabindex="0" role="button"
           aria-label="View ${p.displayName}" style="--project-color:${p.color}">

    <div class="project-card-top">
      <div class="project-type-badge" style="color:${p.color};border-color:${p.color}20;background:${p.color}10">
        ${typeIcon(p.type)} ${p.type}
      </div>
      <span class="badge badge-${statusColor(p.status)}">${p.status}</span>
    </div>

    <h3 class="project-name">${p.displayName}</h3>
    <p class="project-desc">${p.description}</p>

    <div class="project-meta">
      <span class="lang-dot" style="background:${lc}" title="${p.language}"></span>
      <span class="project-lang">${p.language}</span>
      ${p.stars > 0 ? `<span class="project-stat">★ ${p.stars}</span>` : ''}
      ${p.forks > 0 ? `<span class="project-stat">⑂ ${p.forks}</span>` : ''}
    </div>

    <div class="project-topics">
      ${p.topics.slice(0, 4).map(t => `<span class="badge badge-dim">${t}</span>`).join('')}
    </div>

    <div class="project-glow" style="--glow-color:${p.color}"></div>
  </article>
  `;
}

function openProjectDetail(p: Project): void {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  modal.innerHTML = /* html */`
  <div class="modal-content project-detail">
    <button class="modal-close" aria-label="Close">×</button>

    <div class="project-detail-header" style="--project-color:${p.color}">
      <div class="project-detail-type">
        ${typeIcon(p.type)} ${p.type}
      </div>
      <h2>${p.displayName}</h2>
      <a href="${p.repo}" class="repo-link" target="_blank" rel="noopener">${p.repo.replace('https://', '')} ↗</a>
    </div>

    <p class="project-detail-desc">${p.description}</p>

    ${p.installCmd ? `
    <div class="install-block">
      <div class="install-label">Install</div>
      <div class="code-block">
        <code>${p.installCmd}</code>
        <button class="copy-btn" onclick="navigator.clipboard.writeText('${p.installCmd.replace(/'/g, "\\'")}');this.textContent='Copied!';setTimeout(()=>this.textContent='Copy',2000)">Copy</button>
      </div>
    </div>
    ` : ''}

    <div class="project-detail-meta">
      <div class="meta-item"><span class="meta-key">Language</span><span>${p.language}</span></div>
      <div class="meta-item"><span class="meta-key">Status</span><span class="badge badge-${statusColor(p.status)}">${p.status}</span></div>
      <div class="meta-item"><span class="meta-key">Platforms</span><span>${p.platforms.join(', ')}</span></div>
      ${p.stars ? `<div class="meta-item"><span class="meta-key">Stars</span><span>★ ${p.stars}</span></div>` : ''}
    </div>

    <div class="project-topics-full">
      ${p.topics.map(t => `<span class="badge badge-teal">${t}</span>`).join('')}
    </div>

    <div class="app-detail-actions">
      <a href="${p.repo}" class="btn btn-primary" target="_blank" rel="noopener">View on GitHub ↗</a>
      ${p.demoUrl  ? `<a href="${p.demoUrl}"  class="btn btn-secondary" target="_blank" rel="noopener">Live Demo ↗</a>` : ''}
      ${p.docsUrl  ? `<a href="${p.docsUrl}"  class="btn btn-ghost"     target="_blank" rel="noopener">Docs ↗</a>` : ''}
    </div>
  </div>
  `;

  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
  modal.querySelector('.modal-close')!.addEventListener('click', () => modal.remove());
  document.body.appendChild(modal);
  (modal.querySelector('.modal-close') as HTMLElement).focus();
}

function typeIcon(type: string): string {
  const icons: Record<string, string> = {
    'AI App': '🤖', 'CLI Tool': '⚙️', 'Web App': '🌐', 'Desktop App': '🖥️',
    'Game': '🎮', 'Library': '📦', 'Mobile App': '📱', 'Experimental': '🔬',
  };
  return icons[type] ?? '◈';
}

function statusColor(s: string): string {
  return s === 'Active' ? 'green' : s === 'Prototype' ? 'amber' : 'dim';
}
